/**
 * ============================================================================
 * Meta Attribution Tracker for GoKwik - Kauma Platform
 * ============================================================================
 *
 * Problem: GoKwik redirects users to a different domain during checkout,
 * breaking Meta's _fbc and _fbp cookies, causing attribution loss.
 *
 * Solution: Captures fbclid, _fbc, _fbp BEFORE the redirect and stores them
 * in localStorage, cart attributes, and Kauma backend for CAPI recovery.
 *
 * Installation: Add this script to your Shopify theme's <head> section
 *
 * ============================================================================
 */

(function() {
  'use strict';

  // Configuration
  const KAUMA_API_URL = 'https://api.kaumakids.com/tracking/capture';
  const DEBUG = false; // Set to true to see console logs

  // ========================================================================
  // Utility Functions
  // ========================================================================

  function log(...args) {
    if (DEBUG || window.location.search.includes('debug=meta')) {
      console.log('[Meta Tracker]', ...args);
    }
  }

  function getUrlParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  function setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${d.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  }

  // ========================================================================
  // Step 1: Capture Meta Tracking Data
  // ========================================================================

  function captureMetaTracking() {
    const fbclid = getUrlParam('fbclid');
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');

    const data = {
      fbclid: fbclid,
      fbc: fbc,
      fbp: fbp,
      utm_source: getUrlParam('utm_source'),
      utm_medium: getUrlParam('utm_medium'),
      utm_campaign: getUrlParam('utm_campaign'),
      utm_content: getUrlParam('utm_content'),
      utm_term: getUrlParam('utm_term'),
      landing_page: window.location.href,
      referrer: document.referrer,
      timestamp: new Date().toISOString()
    };

    // If fbclid in URL but no _fbc cookie exists, create it
    // Format: fb.1.{timestamp}.{fbclid}
    if (fbclid && !fbc) {
      const fbcValue = `fb.1.${Date.now()}.${fbclid}`;
      setCookie('_fbc', fbcValue, 90); // 90 days
      data.fbc = fbcValue;
      log('Created _fbc cookie:', fbcValue);
    }

    // Store in localStorage for persistence
    try {
      localStorage.setItem('meta_tracking', JSON.stringify(data));
      log('Stored in localStorage:', data);
    } catch (e) {
      log('localStorage error:', e);
    }

    // Also store in sessionStorage as backup
    try {
      sessionStorage.setItem('meta_tracking', JSON.stringify(data));
    } catch (e) {
      log('sessionStorage error:', e);
    }

    return data;
  }

  // ========================================================================
  // Step 2: Attach to Shopify Cart
  // ========================================================================

  function attachToCart(trackingData) {
    // Only attach if we have useful data
    if (!trackingData.fbclid && !trackingData.fbc && !trackingData.fbp) {
      log('No Meta tracking data to attach');
      return;
    }

    // Update cart attributes via Shopify AJAX API
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          meta_fbclid: trackingData.fbclid || '',
          meta_fbc: trackingData.fbc || '',
          meta_fbp: trackingData.fbp || '',
          meta_utm_source: trackingData.utm_source || '',
          meta_utm_campaign: trackingData.utm_campaign || '',
          meta_landing_page: trackingData.landing_page || ''
        }
      })
    })
    .then(response => response.json())
    .then(cart => {
      log('✓ Attached to cart:', cart.attributes);
    })
    .catch(err => {
      log('✗ Failed to attach to cart:', err);
    });
  }

  // ========================================================================
  // Step 3: Send to Kauma Backend
  // ========================================================================

  function sendToKauma(trackingData) {
    // Only send if we have useful data
    if (!trackingData.fbclid && !trackingData.fbc && !trackingData.fbp) {
      log('No Meta tracking data to send');
      return;
    }

    // Get or create external_id (customer ID or session ID)
    let externalId = localStorage.getItem('kauma_external_id');
    if (!externalId) {
      // Generate anonymous session ID
      externalId = 'anon_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('kauma_external_id', externalId);
    }

    // Send to Kauma tracking endpoint
    fetch(KAUMA_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        external_id: externalId,
        ...trackingData
      })
    })
    .then(response => response.json())
    .then(data => {
      log('✓ Sent to Kauma:', data);
    })
    .catch(err => {
      log('✗ Failed to send to Kauma:', err);
    });
  }

  // ========================================================================
  // Step 4: Re-attach on Add to Cart
  // ========================================================================

  function watchAddToCart() {
    // Listen for add-to-cart button clicks
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[name="add"], .add-to-cart, button[type="submit"][name="add"]');
      if (btn) {
        // Give Shopify time to create/update the cart
        setTimeout(() => {
          const stored = localStorage.getItem('meta_tracking');
          if (stored) {
            try {
              const trackingData = JSON.parse(stored);
              attachToCart(trackingData);
            } catch (e) {
              log('Error parsing stored tracking:', e);
            }
          }
        }, 500);
      }
    });
  }

  // ========================================================================
  // Initialize
  // ========================================================================

  function init() {
    log('Initializing Meta Attribution Tracker...');

    // Step 1: Capture tracking data
    const trackingData = captureMetaTracking();

    // Step 2 & 3: Store and send if we have Meta tracking data
    if (trackingData.fbclid || trackingData.fbc || trackingData.fbp) {
      log('Meta tracking detected:', {
        fbclid: trackingData.fbclid ? 'YES' : 'NO',
        fbc: trackingData.fbc ? 'YES' : 'NO',
        fbp: trackingData.fbp ? 'YES' : 'NO'
      });

      // Attach to cart immediately
      attachToCart(trackingData);

      // Send to Kauma backend
      sendToKauma(trackingData);
    } else {
      log('No Meta tracking data found in URL or cookies');
    }

    // Step 4: Watch for add-to-cart events
    watchAddToCart();

    log('Initialization complete');
  }

  // ========================================================================
  // Run when DOM is ready
  // ========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  if (DEBUG) {
    window.MetaTracker = {
      getStoredData: function() {
        const stored = localStorage.getItem('meta_tracking');
        return stored ? JSON.parse(stored) : null;
      },
      captureNow: function() {
        return captureMetaTracking();
      },
      sendNow: function() {
        const data = this.getStoredData();
        if (data) sendToKauma(data);
      }
    };
  }

})();
